/**
 * Challenges
 *
 * Each challenge is a condition evaluated against a ChallengeStats snapshot
 * (derived from existing completion / streak data — no new tracking). Completing
 * a non-repeatable challenge mints its badge and grants its reward once;
 * repeatable (daily / weekly) challenges grant again each fresh period.
 *
 * See CHALLENGES_SYSTEM.md §3. The reward `item`/`box` only ever references real
 * catalog entries, so nothing fabricated can be granted.
 */

import { BOX_DEFINITIONS, type BoxType, type CosmeticCategory } from './surpriseBoxes';
import { basicSectionsMetadata } from '../data/levels/basics';
import { pythonSectionsMetadata } from '../data/levels/python';
import { hebrewSectionsMetadata } from '../data/levels/hebrew';
import { russianSectionsMetadata } from '../data/levels/russian';
import type { SectionMetadata } from '../data/levels/basics';

export type ChallengeCategory =
  | 'skill'
  | 'volume'
  | 'accuracy'
  | 'coding'
  | 'multilingual'
  | 'multikeyboard'
  | 'mastery'
  | 'consistency'
  | 'dedication'
  // One track per course; a badge for each of the course's sections.
  | 'course-basics'
  | 'course-python'
  | 'course-hebrew'
  | 'course-russian';

export interface ChallengeReward {
  points?: number;
  box?: BoxType;
  item?: { category: CosmeticCategory; id: string };
}

/**
 * Snapshot of the stats every challenge condition reads. Built once per
 * evaluation (see ChallengeService.buildChallengeStats).
 */
export interface ChallengeStats {
  /** Distinct levels ever completed. */
  totalLevels: number;
  /** Distinct levels cleared at 100% accuracy. */
  perfectLevels: number;
  /** Distinct levels earned 5 stars on. */
  masteredLevels: number;
  /** Distinct code-based levels completed (the Coding track). */
  codeLevels: number;
  /** Distinct languages the user has practiced in (the Multilingual track). */
  languagesPracticed: number;
  /** Distinct keyboards the user has practiced on (the Multi-keyboard track). */
  keyboardsPracticed: number;
  /**
   * IDs of course sections the user has fully completed (every level in the
   * section cleared at least once). Counted all-time, not from the challenge
   * baseline — finishing a section before challenges shipped still earns the
   * badge. Drives the per-course section-badge tracks.
   */
  completedSectionIds: ReadonlySet<string>;
  /** Best WPM across all words-only (text) levels. */
  bestWpm: number;
  /** Count of distinct text levels whose best WPM is >= the given threshold. */
  wpmLevelCount: (minWpm: number) => number;
  /** Current consecutive-day streak (same calc the streak UI uses). */
  currentStreak: number;
  /** Total distinct days the user has practiced (cumulative, not consecutive). */
  daysPracticed: number;
  /** Level completions logged today (local date). */
  levelsToday: number;
  /** Level completions logged since the start of the current week (Mon). */
  levelsThisWeek: number;
  /**
   * Days (YYYY-MM-DD) that count toward a streak — met the daily goal OR were
   * freeze-saved. Feeds the monthly/yearly calendar badges. (Same "covered"
   * notion the streak UI uses; see globalStreakUtils.)
   */
  coveredDates: Set<string>;
}

export interface ChallengeDefinition {
  id: string;
  category: ChallengeCategory;
  title: string;
  description: string;
  /** Minted on completion (non-repeatable challenges only). */
  badgeId?: string;
  target: number;
  /** Current progress value for this challenge given the stats snapshot. */
  progress: (s: ChallengeStats) => number;
  reward: ChallengeReward;
  /** Omit for one-time; otherwise the reward re-grants each fresh period. */
  repeatable?: 'daily' | 'weekly';
}

const BOX_BY_RARITY = { common: 'common', rare: 'rare', epic: 'epic', legendary: 'legendary' } as const;

/**
 * Per-course section badges. Generated from the (lightweight) section metadata
 * so the catalog stays the single source of truth: add a section and its badge
 * appears automatically. Catalog order = section order, so the Badge Case
 * reveals each course's next unfinished section as the open challenge.
 */
export const COURSE_SECTION_TRACKS: { category: ChallengeCategory; label: string; metas: SectionMetadata[] }[] = [
  { category: 'course-basics', label: 'English', metas: basicSectionsMetadata },
  { category: 'course-python', label: 'Python', metas: pythonSectionsMetadata },
  { category: 'course-hebrew', label: 'Hebrew', metas: hebrewSectionsMetadata },
  { category: 'course-russian', label: 'Russian', metas: russianSectionsMetadata },
];

export const CHALLENGES: ChallengeDefinition[] = [
  // ── Repeatable (the engagement loop) ─────────────────────────────────
  {
    id: 'daily-8',
    category: 'volume',
    title: 'Daily Grind',
    description: 'Complete 8 levels today.',
    target: 8,
    progress: s => s.levelsToday,
    reward: { points: 15 },
    repeatable: 'daily',
  },
  {
    id: 'weekly-40',
    category: 'volume',
    title: 'Weekly Warrior',
    description: 'Complete 40 levels this week.',
    target: 40,
    progress: s => s.levelsThisWeek,
    reward: { box: 'common' },
    repeatable: 'weekly',
  },

  // ── Speed: reach a WPM threshold on 5 WORD levels (target 5 each) ─────
  c('wpm-30', 'skill', 'Reach 30+ WPM on 5 word levels.', 5, s => s.wpmLevelCount(30), 'common'),
  c('wpm-40', 'skill', 'Reach 40+ WPM on 5 word levels.', 5, s => s.wpmLevelCount(40), 'common'),
  c('wpm-55', 'skill', 'Reach 55+ WPM on 5 word levels.', 5, s => s.wpmLevelCount(55), 'rare'),
  c('wpm-70', 'skill', 'Reach 70+ WPM on 5 word levels.', 5, s => s.wpmLevelCount(70), 'rare'),
  c('wpm-85', 'skill', 'Reach 85+ WPM on 5 word levels.', 5, s => s.wpmLevelCount(85), 'epic'),
  c('wpm-100', 'skill', 'Reach 100+ WPM on 5 word levels.', 5, s => s.wpmLevelCount(100), 'legendary'),

  // ── Volume: complete N distinct levels ───────────────────────────────
  c('vol-10', 'volume', 'Complete 10 different levels.', 10, s => s.totalLevels, 'common'),
  c('vol-25', 'volume', 'Complete 25 different levels.', 25, s => s.totalLevels, 'common'),
  c('vol-50', 'volume', 'Complete 50 different levels.', 50, s => s.totalLevels, 'rare'),
  c('vol-100', 'volume', 'Complete 100 different levels.', 100, s => s.totalLevels, 'rare'),
  c('vol-250', 'volume', 'Complete 250 different levels.', 250, s => s.totalLevels, 'epic'),
  c('vol-1000', 'volume', 'Complete 1000 different levels.', 1000, s => s.totalLevels, 'legendary'),

  // ── Accuracy: clear N WORD levels at 100% accuracy ───────────────────
  c('acc-5', 'accuracy', 'Clear 5 word levels at 100% accuracy.', 5, s => s.perfectLevels, 'common'),
  c('acc-10', 'accuracy', 'Clear 10 word levels at 100% accuracy.', 10, s => s.perfectLevels, 'rare'),
  c('acc-25', 'accuracy', 'Clear 25 word levels at 100% accuracy.', 25, s => s.perfectLevels, 'epic'),
  c('acc-50', 'accuracy', 'Clear 50 word levels at 100% accuracy.', 50, s => s.perfectLevels, 'epic'),
  c('acc-100', 'accuracy', 'Clear 100 word levels at 100% accuracy.', 100, s => s.perfectLevels, 'legendary'),

  // ── Coding: complete N code-based levels (the student track) ─────────
  c('code-5', 'coding', 'Complete 5 code-based levels.', 5, s => s.codeLevels, 'common'),
  c('code-10', 'coding', 'Complete 10 code-based levels.', 10, s => s.codeLevels, 'rare'),
  c('code-25', 'coding', 'Complete 25 code-based levels.', 25, s => s.codeLevels, 'epic'),
  c('code-50', 'coding', 'Complete 50 code-based levels.', 50, s => s.codeLevels, 'epic'),
  c('code-100', 'coding', 'Complete 100 code-based levels.', 100, s => s.codeLevels, 'legendary'),

  // ── Multilingual: practice across N different languages ──────────────
  c('ml-2', 'multilingual', 'Practice in 2 different languages.', 2, s => s.languagesPracticed, 'common'),
  c('ml-3', 'multilingual', 'Practice in 3 different languages.', 3, s => s.languagesPracticed, 'rare'),
  c('ml-4', 'multilingual', 'Practice in 4 different languages.', 4, s => s.languagesPracticed, 'epic'),
  c('ml-5', 'multilingual', 'Practice in 5 different languages.', 5, s => s.languagesPracticed, 'legendary'),

  // ── Multi-keyboard: practice across N different keyboards ────────────
  c('mk-2', 'multikeyboard', 'Practice on 2 different keyboards.', 2, s => s.keyboardsPracticed, 'common'),
  c('mk-3', 'multikeyboard', 'Practice on 3 different keyboards.', 3, s => s.keyboardsPracticed, 'rare'),
  c('mk-4', 'multikeyboard', 'Practice on 4 different keyboards.', 4, s => s.keyboardsPracticed, 'epic'),
  c('mk-5', 'multikeyboard', 'Practice on 5 different keyboards.', 5, s => s.keyboardsPracticed, 'legendary'),

  // ── Mastery: earn 5 stars on N levels ────────────────────────────────
  c('mas-5', 'mastery', 'Earn 5 stars on 5 levels.', 5, s => s.masteredLevels, 'common'),
  c('mas-10', 'mastery', 'Earn 5 stars on 10 levels.', 10, s => s.masteredLevels, 'rare'),
  c('mas-25', 'mastery', 'Earn 5 stars on 25 levels.', 25, s => s.masteredLevels, 'epic'),
  c('mas-50', 'mastery', 'Earn 5 stars on 50 levels.', 50, s => s.masteredLevels, 'epic'),
  c('mas-100', 'mastery', 'Earn 5 stars on 100 levels.', 100, s => s.masteredLevels, 'legendary'),

  // ── Consistency: reach an N-day streak ───────────────────────────────
  c('streak-3', 'consistency', 'Reach a 3-day streak.', 3, s => s.currentStreak, 'common'),
  c('streak-5', 'consistency', 'Reach a 5-day streak.', 5, s => s.currentStreak, 'common'),
  c('streak-7', 'consistency', 'Reach a 7-day streak.', 7, s => s.currentStreak, 'rare'),
  c('streak-14', 'consistency', 'Reach a 14-day streak.', 14, s => s.currentStreak, 'rare'),
  c('streak-30', 'consistency', 'Reach a 30-day streak.', 30, s => s.currentStreak, 'epic'),
  c('streak-100', 'consistency', 'Reach a 100-day streak.', 100, s => s.currentStreak, 'legendary'),

  // ── Dedication: practice on N distinct days (cumulative) ─────────────
  c('ded-10', 'dedication', 'Practice on 10 different days.', 10, s => s.daysPracticed, 'common'),
  c('ded-30', 'dedication', 'Practice on 30 different days.', 30, s => s.daysPracticed, 'rare'),
  c('ded-60', 'dedication', 'Practice on 60 different days.', 60, s => s.daysPracticed, 'epic'),
  c('ded-120', 'dedication', 'Practice on 120 different days.', 120, s => s.daysPracticed, 'epic'),
  c('ded-365', 'dedication', 'Practice on 365 different days.', 365, s => s.daysPracticed, 'legendary'),

  // ── Course sections: a prestige badge for completing each section ─────
  // One track (category) per course; one badge per section, earned by
  // clearing every level in that section. Pure prestige — no box/points —
  // and counted all-time (see ChallengeStats.completedSectionIds).
  ...sectionChallenges(),
];

function sectionChallenges(): ChallengeDefinition[] {
  const out: ChallengeDefinition[] = [];
  for (const track of COURSE_SECTION_TRACKS) {
    for (const meta of track.metas) {
      out.push({
        id: `sec-${meta.id}`,
        category: track.category,
        title: `${track.label} · ${meta.title}`,
        description: `Complete every level in ${track.label}: ${meta.title}.`,
        badgeId: `badge-sec-${meta.id}`,
        target: 1,
        progress: s => (s.completedSectionIds.has(meta.id) ? 1 : 0),
        reward: {}, // prestige only
      });
    }
  }
  return out;
}

/** Compact one-time challenge builder. Title comes from the badge (badges.ts). */
function c(
  id: string,
  category: ChallengeCategory,
  description: string,
  target: number,
  progress: (s: ChallengeStats) => number,
  rarity: keyof typeof BOX_BY_RARITY,
): ChallengeDefinition {
  return {
    id,
    category,
    title: id,
    description,
    badgeId: `badge-${id}`,
    target,
    progress,
    reward: { box: BOX_BY_RARITY[rarity] },
  };
}

export function getChallenge(id: string): ChallengeDefinition | undefined {
  return CHALLENGES.find(c => c.id === id);
}

/** The challenge that mints a given badge, if any. */
export function getChallengeByBadgeId(badgeId: string): ChallengeDefinition | undefined {
  return CHALLENGES.find(c => c.badgeId === badgeId);
}

const CATEGORY_LABELS: Record<CosmeticCategory, string> = {
  cursor: 'cursor',
  frame: 'frame',
  finishEffect: 'finish effect',
  premiumAvatar: 'avatar',
  background: 'background',
  gadget: 'gadget',
  presetAvatar: 'avatar',
};

/** Short, catalog-light summary of a reward (for badge chips / lists). */
export function rewardSummary(reward: ChallengeReward): string {
  const parts: string[] = [];
  if (reward.points) parts.push(`🪙 ${reward.points} pts`);
  if (reward.box) parts.push(`🎁 ${BOX_DEFINITIONS[reward.box].label}`);
  if (reward.item) parts.push(`✨ ${CATEGORY_LABELS[reward.item.category]}`);
  return parts.join(' · ');
}
